import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { Form, FormItem } from '@/features/vfo/uikit/Form';
import { Radio } from '@/features/vfo/uikit/Radio';
import { COLORS } from '@/shared/constants/colors';
import { Title } from '@/shared/uikit/Title';
import { RadioGroup } from '@/shared/uikitV2/antd/Radio';
import { Button } from '@/shared/uikitV2/Button';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { AddVfoDocumentModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../../interwill/containers/ModalContainerV1';

type FormValues = {
  uploadType: 'manual' | 'import';
};

export const AddVfoDocumentModal: FC<AddVfoDocumentModalProps> = ({ caseId, onFinish }) => {
  const { t: tc } = useTranslation('common');
  const { t: tw } = useTranslation('widgets/modals', { keyPrefix: 'addVfoDocument' });
  const { control, handleSubmit: formHandleSubmit, watch } = useForm<FormValues>();
  const modals = useModalsStore();

  const hasUploadType = Boolean(watch('uploadType'));

  const onSubmit = ({ uploadType }: FormValues) => {
    modals.closeModal();

    if (uploadType === 'import') {
      modals.openModal({ type: 'VfoImportDocumentModal', defaultCaseId: caseId, onFinish });
    } else {
      modals.openModal({
        type: 'UploadVfoDocumentModal',
        caseId,
        onFinish,
      });
    }
  };

  return (
    <ModalContainerV1 title="Add Document">
      <Form onFinish={formHandleSubmit(onSubmit)}>
        <ModalBody>
          <StyledTitle bold size={16}>
            {tw('description')}
          </StyledTitle>
          <Controller
            control={control}
            name="uploadType"
            rules={{ required: true }}
            render={({ field }) => (
              <FormItem>
                <RadioGroup {...field}>
                  <RadioItems>
                    <Radio value="manual">
                      <RadioLabel>
                        <RadioLabelTitle>{tw('option1.title')}</RadioLabelTitle>
                        <span>{tw('option1.description')}</span>
                      </RadioLabel>
                    </Radio>
                    <Divider />
                    <Radio value="import">
                      <RadioLabel>
                        <RadioLabelTitle>{tw('option2.title')}</RadioLabelTitle>
                        <span>{tw('option2.description')}</span>
                      </RadioLabel>
                    </Radio>
                  </RadioItems>
                </RadioGroup>
              </FormItem>
            )}
          />
        </ModalBody>
        <Buttons>
          <Button type="default" size="middle" htmlType="button" onClick={modals.closeModal}>
            {tc('cancel')}
          </Button>
          <Button size="middle" htmlType="submit" disabled={!hasUploadType}>
            {tc('next')}
          </Button>
        </Buttons>
      </Form>
    </ModalContainerV1>
  );
};

const ModalBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StyledTitle = styled(Title)`
  &&& {
    line-height: 24px;
  }
`;

const Buttons = styled.div`
  padding: 16px 24px;
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: end;
`;

const RadioItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: ${COLORS.systemDark100};
`;

const RadioLabel = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 20px;
`;

const RadioLabelTitle = styled.span`
  font-weight: 700;
`;
